import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import zeplinColor from 'theme/Color';
import Pagination from 'components/featured/AdminPanel/components/UserManagement/UserTable/Pagination';

export type RowData = {
  label: string;
  size: number;
  followers: number;
  instagramDistribution: number;
  avg: number;
  from: string;
  percentage: number;
};

export type SectionData = {
  title: string;
  columnLabel: string;
  rows: RowData[];
};

type DemographicTableProps = {
  sections: SectionData[];
};

const HorizontalBar = ({ value }: { value: number }) => {
  const safeValue = Math.max(0, Math.min(100, value));

  return (
    <Box
      sx={{
        height: '24px',
        backgroundColor: zeplinColor.Neutral3,
        borderRadius: '6px',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: `${safeValue}%`,
          background: '#F3A03F',
          borderRadius: '6px',
        }}
      />
    </Box>
  );
};

const DemographicTable = ({ sections }: DemographicTableProps) => {
  const safeSections = Array.isArray(sections) ? sections : [];

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageWindowStart, setPageWindowStart] = useState(0);

  // paginate each section by same page window
  const startIndex = currentPage * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const paginatedSections: SectionData[] = safeSections
    .map((section) => ({
      ...section,
      rows: (section.rows || []).slice(startIndex, endIndex),
    }))
    .filter((section) => section.rows.length > 0);

  // total pages based on longest section
  const totalCount = safeSections.reduce(
    (max, section) => Math.max(max, section.rows?.length || 0),
    0,
  );
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  // rows shown in current view
  const paginatedDataLength = paginatedSections.reduce(
    (sum, section) => sum + section.rows.length,
    0,
  );

  const handlePreviousPage = () => setCurrentPage((prev) => Math.max(0, prev - 1));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  const handleFirstPage = () => {
    setCurrentPage(0);
    setPageWindowStart(0);
  };
  const handleLastPage = () => {
    setCurrentPage(totalPages - 1);
    setPageWindowStart(Math.max(0, totalPages - 4));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>
      <Box
        sx={{
          borderRadius: '14px',
          border: `1px solid ${zeplinColor.Neutral5}`,
          overflow: 'hidden',
          flex: 1,
        }}
      >
        <Box
          sx={{
            maxHeight: 'calc(100vh - 400px)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: zeplinColor.Neutral5,
              borderRadius: '3px',
            },
          }}
        >
          {paginatedSections.map((section, sectionIdx) => (
            <Box key={section.title}>
              {/* Section Header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(160px, 1.3fr) 80px 1fr 1fr 80px 120px 60px',
                  gap: '12px',
                  alignItems: 'center',
                  backgroundColor: zeplinColor.Neutral3,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                <Typography
                  variant='responsiveBody3'
                  sx={{ padding: '12px 16px', fontWeight: 600 }}
                >
                  {section.columnLabel}
                </Typography>
                <Typography
                  variant='responsiveBody3'
                  sx={{ padding: '12px 16px', fontWeight: 600 }}
                >
                  Size
                </Typography>
                <Typography
                  variant='responsiveBody3'
                  sx={{
                    borderLeft: `1px solid ${zeplinColor.Neutral5}`,
                    borderRight: `1px solid ${zeplinColor.Neutral5}`,
                    padding: '12px 16px',
                    fontWeight: 600,
                  }}
                >
                  Followers
                </Typography>
                <Typography
                  variant='responsiveBody3'
                  sx={{ padding: '12px 16px', fontWeight: 600 }}
                >
                  Instagram Distribution
                </Typography>
                <Typography
                  variant='responsiveBody3'
                  sx={{ padding: '12px 16px', fontWeight: 600 }}
                >
                  Avg
                </Typography>
                <Typography
                  variant='responsiveBody3'
                  sx={{ padding: '12px 16px', fontWeight: 600 }}
                >
                  From
                </Typography>
                <Typography
                  variant='responsiveBody3'
                  sx={{ padding: '12px 16px', fontWeight: 600 }}
                >
                  Delete
                </Typography>
              </Box>

              {/* Section Rows */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  borderBottom:
                    sectionIdx < paginatedSections.length - 1
                      ? `1px solid ${zeplinColor.Neutral5}`
                      : 'none',
                }}
              >
                {section.rows.map((row, idx) => (
                  <Box
                    key={`${section.title}-${idx}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(160px, 1.3fr) 80px 1fr 1fr 80px 120px 60px',
                      gap: '12px',
                      alignItems: 'center',
                      borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                      borderLeft: `1px solid ${zeplinColor.Neutral5}`,
                      borderRight: `1px solid ${zeplinColor.Neutral5}`,
                    }}
                  >
                    <Typography variant='responsiveBody3' sx={{ padding: '12px 16px' }}>
                      {row?.label}
                    </Typography>
                    <Typography variant='responsiveBody3' sx={{ padding: '12px 16px' }}>
                      {row?.size}
                    </Typography>
                    <Box
                      sx={{
                        borderLeft: `1px solid ${zeplinColor.Neutral5}`,
                        borderRight: `1px solid ${zeplinColor.Neutral5}`,
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        display: 'flex',
                        gap: '4px',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant='responsiveBody3' sx={{ padding: '12px 16px' }}>
                        {row?.percentage}%
                      </Typography>
                      <HorizontalBar value={row?.percentage} />
                    </Box>
                    <HorizontalBar value={row?.instagramDistribution} />
                    <Typography variant='responsiveBody3' sx={{ padding: '12px 16px' }}>
                      {row?.avg.toFixed(1)}%
                    </Typography>
                    <Typography variant='responsiveBody3' sx={{ padding: '12px 16px' }}>
                      {row?.from}%
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: '12px 16px' }}>
                      <Typography variant='responsiveBody3' sx={{ padding: '12px 16px' }}>
                        {row?.from}%
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Pagination */}
      <Pagination
        setCurrentPage={setCurrentPage}
        setRowsPerPage={setRowsPerPage}
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        count={totalCount}
        paginatedDataLength={paginatedDataLength}
        onFirstPage={handleFirstPage}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onLastPage={handleLastPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={(newRows) => {
          setRowsPerPage(newRows);
          setCurrentPage(0);
          setPageWindowStart(0);
        }}
        pageWindowStart={pageWindowStart}
        setPageWindowStart={setPageWindowStart}
      />
    </Box>
  );
};

export default DemographicTable;
